import * as THREE from 'three';
import { OrbitControls } from 'three/addons/controls/OrbitControls.js';
import { RGBELoader } from 'three/addons/loaders/RGBELoader.js';
import { Pane } from 'tweakpane';



class App {

  #threejs_ = null;
  #camera_ = null;
  #scene_ = null;
  #clock_ = null;
  #controls_ = null;

  #debugUI_ = null;

  constructor() {
  }

  async initialize() {
    this.#clock_ = new THREE.Clock(true);

    window.addEventListener('resize', () => {
      this.#onWindowResize_();
    }, false);

    await this.#setupProject_();

    this.#onWindowResize_();
    this.#raf_();
  }

  async #setupProject_() {
    await this.#setupRenderer_();

    // Initialize project
    const projectFolder = this.#debugUI_.addFolder({ title: 'Project', expanded: false });

    await this.onSetupProject(projectFolder);
  }

  async #setupRenderer_() {
    this.#threejs_ = new THREE.WebGLRenderer( { antialias: true } );
    this.#threejs_.shadowMap.enabled = true;
    this.#threejs_.shadowMap.type = THREE.PCFSoftShadowMap;
    this.#threejs_.toneMapping = THREE.ACESFilmicToneMapping;
    this.#threejs_.setSize(window.innerWidth, window.innerHeight);
    document.body.appendChild(this.#threejs_.domElement);

    this.#debugUI_ = new Pane();

    const fov = 60;
    const aspect = window.innerWidth / window.innerHeight;
    const near = 0.1;
    const far = 1000;
    this.#camera_ = new THREE.PerspectiveCamera(fov, aspect, near, far);
    this.#camera_.position.set(20, 1, 2);
    this.#camera_.lookAt(new THREE.Vector3(0, 0, 0));

    this.#controls_ = new OrbitControls(this.#camera_, this.#threejs_.domElement);
    this.#controls_.enableDamping = true;
    this.#controls_.target.set(0, 8, 0);
    this.#controls_.update();

    this.#scene_ = new THREE.Scene();
    this.#scene_.background = new THREE.Color(0x000000);

    // Scene tweaks
    this.#scene_.backgroundBlurriness = 0.0;
    this.#scene_.backgroundIntensity = 0.3;
    this.#scene_.environmentIntensity = 0.025;
    const sceneFolder = this.#debugUI_.addFolder({ title: 'Scene', expanded: false });
    sceneFolder.addBinding(this.#scene_, 'backgroundBlurriness', { min: 0.0, max: 1.0 });
    sceneFolder.addBinding(this.#scene_, 'backgroundIntensity', { min: 0.0, max: 1.0 });
    sceneFolder.addBinding(this.#scene_, 'environmentIntensity', { min: 0.0, max: 1.0 });
  }

  #onWindowResize_() {
    // Intentionally ignoring DPR for now
    // const dpr = window.devicePixelRatio;
    const dpr = 1;

    const canvas = this.#threejs_.domElement;
    canvas.style.width = window.innerWidth + 'px';
    canvas.style.height = window.innerHeight + 'px';
    const w = canvas.clientWidth;
    const h = canvas.clientHeight;

    const aspect = w / h;

    this.#threejs_.setSize(w, h, false);
    this.#camera_.aspect = aspect;
    this.#camera_.updateProjectionMatrix();
  }

  #raf_() {
    requestAnimationFrame((t) => {
      const timeElapsed = Math.min(this.#clock_.getDelta(), 0.1);
      this.#step_(timeElapsed);
      this.#render_();
      this.#raf_();
    });
  }

  #render_() {
    this.onRender();
    this.#threejs_.render(this.#scene_, this.#camera_);
  }

  #step_(timeElapsed) {
    this.#controls_.update(timeElapsed);
    this.onStep(timeElapsed, this.#clock_.getElapsedTime());
  }

  loadRGBE(path) {
    const rgbeLoader = new RGBELoader();
    rgbeLoader.load(path , (hdrTexture) => {
      hdrTexture.mapping = THREE.EquirectangularReflectionMapping;
  
      this.#scene_.background = hdrTexture;
      this.#scene_.environment = hdrTexture;
    });
  }

  // Override these methods
  async onSetupProject() {
  }

  onRender() {
  }

  onStep(timeElapsed, totalTimeElapsed) {
  }

  onResize() {
  }

  // Getters
  get Scene() { return this.#scene_; }
  get Camera() { return this.#camera_; }

  // ADD THESE:
  get Renderer() { return this.#threejs_; }
  get OrbitControls() { return this.#controls_; }
}


export { App };